import React, { useState, useEffect } from 'react';
import contextAnalyticsService from '../../../services/contextAnalyticsService';
import AgentActivityTable from './AgentActivityTable';
import RAGStatisticsCard from './RAGStatisticsCard';
import KnowledgeIndexingQueue from './KnowledgeIndexingQueue';
import InteractionTimeline from './InteractionTimeline';
import ContextCacheMonitor from './ContextCacheMonitor';
import {
  ChartBarIcon,
  CpuChipIcon,
  ServerIcon,
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AgentsContext = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [serviceError, setServiceError] = useState(null);

  // Data states
  const [performanceOverview, setPerformanceOverview] = useState(null);
  const [agentUsage, setAgentUsage] = useState([]);
  const [ragStats, setRAGStats] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);

  // Filter states
  const [dateRange, setDateRange] = useState('today');
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Helper function to sanitize objects - convert any object to string
  const sanitizeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return value;
    if (typeof value === 'object') {
      if (value.name) return value.name;
      const keys = Object.keys(value);
      return keys.length > 0 ? keys[0] : 'N/A';
    }
    return String(value);
  };

  // Recursively sanitize all data
  const sanitizeData = (data) => {
    if (data === null || data === undefined) return data;
    if (Array.isArray(data)) {
      return data.map(item => sanitizeData(item));
    }
    if (typeof data === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        if ((key === 'department' || key === '_id') && typeof value === 'object' && value !== null) {
          sanitized[key] = sanitizeValue(value);
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          sanitized[key] = sanitizeData(value);
        } else if (Array.isArray(value)) {
          sanitized[key] = value.map(item => sanitizeData(item));
        } else {
          sanitized[key] = value;
        }
      }
      return sanitized;
    }
    return data;
  };

  // Normalize department field to string
  const normalizeDepartment = (dept) => {
    if (typeof dept === 'string') return dept;
    if (typeof dept === 'object' && dept !== null) {
      return dept.name || Object.keys(dept)[0] || 'N/A';
    }
    return 'N/A';
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [dateRange, selectedAgent]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      setServiceError(null);

      const filters = buildFilters();

      const [perfResponse, usageResponse, ragResponse, cacheResponse] = await Promise.all([
        contextAnalyticsService.getPerformanceOverview(filters),
        contextAnalyticsService.getAgentContextUsage(filters),
        contextAnalyticsService.getRAGStatistics(),
        contextAnalyticsService.getCacheStatistics(filters)
      ]);

      if (perfResponse) {
        setPerformanceOverview(perfResponse.data || perfResponse);
      }

      if (usageResponse) {
        const usageData = usageResponse.data || usageResponse;
        const normalizedUsage = Array.isArray(usageData) ? usageData.map(agent => ({
          ...agent,
          department: normalizeDepartment(agent.department)
        })) : [];
        setAgentUsage(normalizedUsage);
      }

      if (ragResponse) {
        const ragData = ragResponse.data || ragResponse;
        setRAGStats(sanitizeData(ragData));
      }

      if (cacheResponse) {
        setCacheStats(cacheResponse.data || cacheResponse);
      }

    } catch (error) {
      logger.error('Error fetching context analytics:', error);

      // Detect service unavailable vs auth error
      const status = error.response?.status;
      if (!status || status >= 500 || error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        setServiceError('Context service is unavailable. Analytics data cannot be loaded.');
      } else if (status === 401 || status === 403) {
        setServiceError('Authentication failed. Please refresh the page and try again.');
      } else {
        setServiceError(`Failed to load analytics: ${error.message}`);
      }

      if (!silent) {
        showNotification?.('Failed to load context analytics', 'error');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const buildFilters = () => {
    const filters = {};
    const now = Date.now();

    switch (dateRange) {
      case 'today': {
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        filters.startDate = today.toISOString();
        break;
      }
      case 'week':
        filters.startDate = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'month':
        filters.startDate = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default:
        break;
    }

    if (selectedAgent) {
      filters.agentId = selectedAgent;
    }

    return filters;
  };

  const handleRefresh = () => {
    fetchData();
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'activity', name: 'Agent Activity', icon: CpuChipIcon },
    { id: 'rag', name: 'RAG Statistics', icon: ServerIcon },
    { id: 'cache', name: 'Cache Performance', icon: ClockIcon },
    { id: 'interactions', name: 'Interactions', icon: ChartBarIcon }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Agents Context Analytics
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitor RAG data fetching, agent activity, and context performance
          </p>
        </div>

        <div className="flex space-x-3">
          {/* Date Range Filter */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="rounded-md border-gray-300 dark:border-gray-800 dark:bg-[#2a2a2a] dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-800 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-[#222222] hover:bg-gray-50 dark:hover:bg-[#2a2a2a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Service Error Banner */}
      {serviceError && (
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">{serviceError}</p>
              <button
                onClick={handleRefresh}
                className="mt-2 text-sm font-medium text-yellow-700 dark:text-yellow-300 underline hover:text-yellow-600"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-700'
                  }
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
        {activeTab === 'overview' && (
          <div className="space-y-6 pb-6">
            {/* Performance Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Queries</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {performanceOverview?.queries?.totalQueries || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Response Time</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {performanceOverview?.queries?.avgDuration
                        ? `${Math.round(performanceOverview.queries.avgDuration)}ms`
                        : '0ms'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ServerIcon className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cache Hit Rate</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {cacheStats?.hitRate || 0}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CpuChipIcon className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Agents</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {agentUsage?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Usage Table */}
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Agent Context Usage
                </h3>
              </div>
              <div className="p-6">
                {agentUsage && agentUsage.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Agent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Department
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Queries
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Tokens
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Cache Hit Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Avg Response
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {agentUsage.map((agent, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-[#222222]">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {agent.agentName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {agent.department}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {agent.totalQueries}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {agent.totalTokens?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {agent.cacheHitRate?.toFixed(1)}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {Math.round(agent.avgResponseTime)}ms
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              ${agent.estimatedCost?.toFixed(4)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No agent usage data available for the selected period.
                  </p>
                )}
              </div>
            </div>

            {/* RAG & Cache Quick Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RAGStatisticsCard ragStats={ragStats} />
              <ContextCacheMonitor cacheStats={cacheStats} compact={true} />
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <AgentActivityTable
            dateRange={dateRange}
            selectedAgent={selectedAgent}
            showNotification={showNotification}
          />
        )}

        {activeTab === 'rag' && (
          <div className="space-y-6">
            <RAGStatisticsCard ragStats={ragStats} />
            <KnowledgeIndexingQueue showNotification={showNotification} />
          </div>
        )}

        {activeTab === 'cache' && (
          <ContextCacheMonitor
            cacheStats={cacheStats}
            dateRange={dateRange}
            showNotification={showNotification}
          />
        )}

        {activeTab === 'interactions' && (
          <InteractionTimeline
            dateRange={dateRange}
            selectedAgent={selectedAgent}
            showNotification={showNotification}
          />
        )}
      </div>
    </div>
  );
};

export default AgentsContext;
